import { useContext, useState } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import { auth } from "../../api_config/firebase-configuration";
import { setUserPersistence } from "../lib/global";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { FacebookButton, GoogleButton, TwitterButton } from "./SocialButtons";
import { RegisterPage } from "../types/cmsLocalizationTypes";
import { LocalizationContext } from "../lib/context";

export function Credentials({
  localizedPageContent,
}: {
  localizedPageContent: RegisterPage;
}) {
  const { localeStrings } = useContext(LocalizationContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptError, setAcceptError] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const createUserWithPw = async () => {
    if (!accepted)
      return setAcceptError("please accept the terms and conditions");
    try {
      const userCredentials = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
    } catch (e: any) {
      setErrorMessage(e.code.split("/")[1].split("-").join(" "));
      return null;
    }
    return null;
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
        }}
      >
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: "auto",
            mx: "auto",
            py: 2,
            gap: 2,
            borderRadius: 2,
          }}
        >
          <>
            <Box
              sx={{
                display: { xs: "none", sm: "block" },
              }}
            >
              <Typography
                variant="h1"
                textAlign="center"
                color="error"
                sx={{ mb: 4 }}
              >
                {localizedPageContent.registerTitle}
              </Typography>
              <Typography variant="h2" textAlign="center">
                {localizedPageContent.register}
              </Typography>
              <Typography variant="h3" textAlign="center">
                {localizedPageContent.makeYourselfVisible}
              </Typography>
            </Box>
            <Typography
              variant="h2"
              textAlign="center"
              color="error"
              sx={{ mb: 4, display: { xs: "block", sm: "none" } }}
            >
              {localizedPageContent.registerTitle}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                maxWidth: 330,
                mx: { xs: 0, sm: 6 },
              }}
            >
              <TextField
                required
                id="email"
                label={localeStrings.uiGeneral.email}
                name="email"
                autoComplete="email"
                error={!!errorMessage}
                helperText={errorMessage}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setErrorMessage("");
                }}
              />

              <FormControl variant="outlined">
                <InputLabel htmlFor="password">
                  {localeStrings.uiGeneral.password}
                </InputLabel>
                <OutlinedInput
                  required
                  name="password"
                  id="password"
                  label="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  onChange={(event) => setPassword(event.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  value="remember"
                  color="primary"
                  defaultChecked={true}
                />
              }
              label={localeStrings.uiGeneral.staySignedIn}
              onChange={(event: any) =>
                setUserPersistence(event.target.checked)
              }
            />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  value="remember"
                  color="primary"
                  defaultChecked={false}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <Typography>
                  {localeStrings.uiGeneral.iAccept}{" "}
                  <a
                    target="blank"
                    href="/assets/docs/General_Terms_And_Conditions_Official.pdf"
                  >
                    {localeStrings.uiGeneral.termsAndConditionsLink}
                  </a>{" "}
                  {localeStrings.uiGeneral.and}{" "}
                  <a target="blank" href="/assets/docs/GDPR_EN.pdf">
                    {localeStrings.uiGeneral.gdprLink}
                  </a>
                  .
                </Typography>
              </Box>
              {!accepted && (
                <Typography color="error">{acceptError}</Typography>
              )}
            </Box>

            <Button
              disabled={!accepted}
              variant="contained"
              data-color="primary"
              onClick={() => createUserWithPw()}
            >
              {localizedPageContent.createAccountButton}
            </Button>
            <Box
              sx={{
                width: "100%",
                maxWidth: 330,
              }}
            >
              <Divider className="py-large">
                <p className="caption">{localeStrings.uiGeneral.or}</p>
              </Divider>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: 100,
                maxWidth: 330,
                width: "100%",
                gap: 1,
              }}
            >
              <GoogleButton
                text={localeStrings.enterPage.googleLogInButton}
                disabled={!accepted}
                action="login"
              />
              <FacebookButton
                text={localeStrings.enterPage.facebookLogInButton}
                disabled={!accepted}
                action="login"
              />
              <TwitterButton
                text={localeStrings.enterPage.twitterLogInButton}
                disabled={!accepted}
                action="login"
              />
              {/* <YahooButton />*/}
            </Box>
            <Box
              sx={{
                zIndex: "2",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography variant="caption" textAlign="center">
                {localizedPageContent.acceptText}
              </Typography>
              <Link href="/enter" variant="body2" textAlign="center">
                {localizedPageContent.alreadyHaveAccountText}
              </Link>
            </Box>
          </>
        </Box>
      </Box>
    </>
  );
}
